import { NA_VALUE } from "@/const/default-values"
import { isTerminatedApplication } from "@/domain/application"
import { getIssuesCountMapOrderedSeverity } from "@/domain/vulnerability"
import {
  DEFAULT_LONG_FORMAT,
  getDateInUserLocale,
  getRelativeTimeFromNow,
  toMs
} from "@/helpers/date.helpers"
import { getBooleanValuesFromInsights } from "@/helpers/insights.helper"
import { getLastDetectedCellData } from "@/helpers/table.helpers"
import { IMAGE_DRAWER_ID } from "@/organisms/Drawers/const"

import type {
  ApplicationAuthentication,
  ApplicationResponse,
  ToolInvocationsResponse
} from "@/api/useInventory.types"

export const getApplicationTabData = (response: ApplicationResponse) => {
  const { data = [], metadata } = response || {}

  return {
    metadata,
    data: data.map((record, index) => {
      const { data: dataRecord, id, displayName } = record || {}
      const { issuesSummary, insights } = dataRecord
      const issuesCounts = getIssuesCountMapOrderedSeverity(issuesSummary)
      const relatedImages = (dataRecord.relatedImages || []).map((image) => ({
        drawerEntityId: image.id,
        drawerId: IMAGE_DRAWER_ID,
        ...image
      }))
      const relatedImagesExist = !!dataRecord.relatedImages?.length
      const firstRelatedImageName = relatedImagesExist
        ? dataRecord.relatedImages[0].name
        : ""
      const namespace = dataRecord.namespace || NA_VALUE
      const lastSeenTimestamp = toMs(dataRecord.lastSeen)
      const creationTimestamp = toMs(dataRecord.creationTimestamp)

      return {
        key: id,
        displayName,
        environmentAndNamespace: `${dataRecord.environment}/${namespace}`,
        lastSeenUserLocale: lastSeenTimestamp
          ? getDateInUserLocale(lastSeenTimestamp, DEFAULT_LONG_FORMAT)
          : NA_VALUE,
        lastSeenRelative: lastSeenTimestamp
          ? getRelativeTimeFromNow(lastSeenTimestamp)
          : NA_VALUE,
        creationDateUserLocale: creationTimestamp
          ? getDateInUserLocale(creationTimestamp, DEFAULT_LONG_FORMAT)
          : NA_VALUE,
        ...dataRecord,
        namespace,
        relatedImages: {
          id: `relatedImages-${index}`,
          count: relatedImages.length - 1,
          label: firstRelatedImageName,
          items: relatedImages,
          copyable: true,
          width: "100%"
        },
        queryParamsRelatedImagesExternalLink: new URLSearchParams({
          namespace,
          applicationName: displayName,
          environment: dataRecord.environment
        }).toString(),
        severity: issuesCounts,
        insights: getBooleanValuesFromInsights(insights),
        disableDrawer: isTerminatedApplication(dataRecord.workloadStatus),
        ...getLastDetectedCellData(record)
      }
    })
  }
}

// The rows describing how callers authenticate to an agent application: the type, and the
// JWT authorizer settings when it validates a custom JWT. Empty when the API sent none.
export const getAuthenticationItems = (
  authentication?: ApplicationAuthentication | null
) => {
  if (!authentication?.type) return []

  const { type, discoveryUrl, allowedAudiences, allowedClients } =
    authentication
  const items = [{ key: "type", value: type }]

  if (type !== "customJwt") return items

  return [
    ...items,
    { key: "discoveryUrl", value: discoveryUrl || NA_VALUE },
    {
      key: "allowedAudiences",
      value: allowedAudiences?.length ? allowedAudiences.join(", ") : NA_VALUE
    },
    {
      key: "allowedClients",
      value: allowedClients?.length ? allowedClients.join(", ") : NA_VALUE
    }
  ]
}

const DECISION_STATUS = {
  allow: "success",
  block: "failed",
  observe: "neutral"
} as const

const formatSeen = (timestamp?: number) => {
  const ms = toMs(timestamp)

  return ms ? getDateInUserLocale(ms, DEFAULT_LONG_FORMAT) : NA_VALUE
}

export const getToolInvocationsData = (response: ToolInvocationsResponse) => {
  const { data = [], metadata } = response || {}

  return {
    metadata: {
      total: metadata?.total ?? data.length,
      blocked: metadata?.blocked ?? 0,
      invocations: metadata?.invocations,
      blockedInvocations: metadata?.blockedInvocations,
      droppedEntries: metadata?.droppedEntries ?? 0
    },
    data: data.map((invocation, index) => ({
      ...invocation,
      key: `${invocation.qualname || invocation.tool}-${invocation.target}-${invocation.decision}-${invocation.ruleVersion}-${index}`,
      toolName: invocation.qualname || invocation.tool || NA_VALUE,
      accessTypeKey: `inventory.application.drawer.runtimeActions.accessTypes.${invocation.accessType || "none"}`,
      decisionTag: {
        tKey: `inventory.application.drawer.runtimeActions.decisions.${invocation.decision || "unknown"}`,
        status: invocation.decision
          ? DECISION_STATUS[invocation.decision]
          : ("neutral" as const)
      },
      target: invocation.target || NA_VALUE,
      firstSeenUserLocale: formatSeen(invocation.firstSeen),
      lastSeenUserLocale: formatSeen(invocation.lastSeen)
    }))
  }
}
